import { keepPreviousData } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Activity,
  AlertTriangle,
  Building2,
  Calendar,
  CheckCircle,
  Clock,
  Eye,
  FileText,
  Hash,
  MessageSquare,
  MoreHorizontal,
  Stethoscope,
  User,
  UserPlus,
} from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

import {
  type ListB2BSecondOpinions,
  listB2BSecondOpinionsSchema,
} from '@virtual-care/client';
import {
  displayName,
  formatDateTimeShort,
  getDoctorName,
} from '@virtual-care/isomorphic';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  cn,
} from '@virtual-care/shadcn';
import {
  RESERVATION_STATUS_META,
  ReservationStatus,
  SearchInput,
  TableRowSort,
  useData,
} from '@virtual-care/ui';

import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import { AssignSecondOpinionDoctorDialog } from './assign-second-opinion-doctor-dialog.tsx';
import { AttachedFiles } from './attached-files.tsx';
import { PatientInfo } from './patient-info.tsx';
import { ReassignSecondOpinionDoctorDialog } from './reassign-second-opinion-doctor-dialog.tsx';

type SecondOpinionRecord = ListB2BSecondOpinions['records'][number];

const wasReferred = (secondOpinion: SecondOpinionRecord) =>
  secondOpinion.timeline?.some(
    (e) => e.type === 'REFERRED_TO_ANOTHER_DOCTOR',
  ) ?? false;

const statusLabel = (status: string) =>
  RESERVATION_STATUS_META[status as keyof typeof RESERVATION_STATUS_META]?.en ??
  status;

const StatusEnum = listB2BSecondOpinionsSchema.shape.status;

export default function B2BSecondOpinionPage() {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true },
  ]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<
    z.infer<typeof StatusEnum> | undefined
  >();

  const [selectedSecondOpinion, setSelectedSecondOpinion] =
    useState<SecondOpinionRecord | null>(null);
  const [showCaseDetails, setShowCaseDetails] = useState(false);
  const [showAssignDoctor, setShowAssignDoctor] = useState(false);
  const [showReassignDoctor, setShowReassignDoctor] = useState(false);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const dataQuery = useData(
    'GET /b2b/second-opinions',
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      status: statusFilter,
      query: searchQuery || undefined,
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  const handleAssignDoctor = (secondOpinion: SecondOpinionRecord) => {
    setSelectedSecondOpinion(secondOpinion);
    setShowAssignDoctor(true);
  };

  const handleReassignDoctor = (secondOpinion: SecondOpinionRecord) => {
    setSelectedSecondOpinion(secondOpinion);
    setShowReassignDoctor(true);
  };

  const columns: ColumnDef<SecondOpinionRecord>[] = [
    {
      accessorKey: 'counter',
      enableSorting: false,
      header: () => {
        return 'Case #';
      },
      cell: ({ row }) => (
        <div className="font-mono font-medium">#{row.getValue('counter')}</div>
      ),
    },
    {
      id: 'companyName',
      enableSorting: false,
      header: 'Company',
      cell: ({ row }) => {
        const patient = row.original.patient;
        return (
          <div className="text-sm font-medium">
            {patient.b2bProfile?.name ?? '—'}
          </div>
        );
      },
    },
    {
      id: 'patientName',
      enableSorting: false,
      header: 'Patient Name',
      cell: ({ row }) => {
        return (
          <div className="text-sm font-medium">
            {displayName(row.original.patient)}
          </div>
        );
      },
    },
    {
      id: 'doctorName',
      accessorFn: (row) => {
        if (!row.doctor || !row.doctorId) return '';
        return `${row.doctor.firstName} ${row.doctor.lastName}`;
      },
      enableSorting: false,
      header: 'Assigned Doctor',
      cell: ({ row }) => {
        const doctor = row.original.doctor;
        const doctorId = row.original.doctorId;
        return doctor && doctorId ? (
          <div className="text-sm">
            {doctor.firstName} {doctor.lastName}
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">Not assigned</span>
        );
      },
    },
    {
      id: 'speciality',
      accessorKey: 'speciality.name',
      enableSorting: false,
      header: 'Speciality',
      cell: ({ row }) => (
        <div className="font-medium">{row.original.speciality.name}</div>
      ),
    },
    {
      accessorKey: 'status',
      enableSorting: false,
      header: 'Status',
      cell: ({ row }) => {
        const secondOpinion = row.original;
        const urgent = secondOpinion.urgent;

        return (
          <div className="flex items-center gap-2">
            <ReservationStatus.Root status={secondOpinion.status} />
            {urgent && (
              <Badge variant="destructive" className="text-xs">
                <AlertTriangle className="mr-1 h-3 w-3" />
                Urgent
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      enableSorting: false,
      header: () => {
        return 'Created';
      },
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2">
            <div className="text-sm">
              {formatDateTimeShort(row.original.createdAt)}
            </div>
          </div>
        );
      },
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => {
        const secondOpinion = row.original;
        const hasDoctor = !!secondOpinion.doctorId;
        const canAssignDoctor =
          !hasDoctor && !['FINISHED'].includes(secondOpinion.status);

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Case Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />

              <DropdownMenuItem
                onClick={() => {
                  setSelectedSecondOpinion(secondOpinion);
                  setShowCaseDetails(true);
                }}
              >
                <Eye className="mr-2 h-4 w-4" />
                View case details
              </DropdownMenuItem>

              {canAssignDoctor && !wasReferred(secondOpinion) && (
                <DropdownMenuItem
                  onClick={() => handleAssignDoctor(secondOpinion)}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Assign doctor
                </DropdownMenuItem>
              )}

              {wasReferred(secondOpinion) && !hasDoctor && (
                <DropdownMenuItem
                  onClick={() => handleReassignDoctor(secondOpinion)}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Assign to new doctor
                </DropdownMenuItem>
              )}

              {!hasDoctor && ['FINISHED'].includes(secondOpinion.status) && (
                <DropdownMenuItem disabled className="text-muted-foreground">
                  <UserPlus className="mr-2 h-4 w-4" />
                  Cannot assign doctor to finished case
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigator.clipboard.writeText(secondOpinion.id)}
                className="text-muted-foreground"
              >
                Copy case ID
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data: dataQuery.data?.records ?? [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <div className="w-full">
      <CountablePageHeader
        title="B2B Second Opinions"
        totalCount={`${dataQuery.data?.totalCount ?? 0} total consultations`}
        icon={Building2}
      />

      {dataQuery.data && (
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="bg-card border-border rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <Clock className="size-4 text-yellow-600 dark:text-yellow-400" />
              <div>
                <div className="text-muted-foreground text-sm">Waiting</div>
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {
                    dataQuery.data.records.filter(
                      (r) => r.status === 'WAITING_DOCTOR_ASSIGNMENT',
                    ).length
                  }
                </div>
              </div>
            </div>
          </div>
          <div className="bg-card border-border rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="size-4 text-green-600 dark:text-green-400" />
              <div>
                <div className="text-muted-foreground text-sm">Confirmed</div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {
                    dataQuery.data.records.filter(
                      (r) => r.status === 'CONFIRMED',
                    ).length
                  }
                </div>
              </div>
            </div>
          </div>
          <div className="bg-card border-border rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <User className="size-4 text-blue-600 dark:text-blue-400" />
              <div>
                <div className="text-muted-foreground text-sm">Unassigned</div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {dataQuery.data.records.filter((r) => !r.doctorId).length}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-card border-border rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-destructive size-4" />
              <div>
                <div className="text-muted-foreground text-sm">Urgent</div>
                <div className="text-destructive text-2xl font-bold">
                  {dataQuery.data.records.filter((r) => r.urgent).length}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 flex items-center gap-4">
        <div className="flex-1">
          <SearchInput
            placeholder="Search by company name, patient name, or description..."
            value={searchQuery}
            onChange={setSearchQuery}
            onClear={() => setSearchQuery('')}
            isLoading={dataQuery.isFetching}
          />
        </div>
        <Select
          value={statusFilter ?? 'all'}
          onValueChange={(v) =>
            setStatusFilter(v === 'all' ? undefined : StatusEnum.parse(v))
          }
        >
          <SelectTrigger className="min-h-11">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="WAITING_DOCTOR_ASSIGNMENT">
              {statusLabel('WAITING_DOCTOR_ASSIGNMENT')}
            </SelectItem>
            <SelectItem value="WAITING_DOCTOR_CONFIRMATION">
              {statusLabel('WAITING_DOCTOR_CONFIRMATION')}
            </SelectItem>
            <SelectItem value="CONFIRMED">
              {statusLabel('CONFIRMED')}
            </SelectItem>
            <SelectItem value="FINISHED">{statusLabel('FINISHED')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </TableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading second opinions...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No B2B second opinions found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />

      {selectedSecondOpinion && (
        <>
          <B2BSecondOpinionDetailsDialog
            selectedSecondOpinion={selectedSecondOpinion}
            open={showCaseDetails}
            onOpenChange={setShowCaseDetails}
          />

          <AssignSecondOpinionDoctorDialog
            selectedSecondOpinion={selectedSecondOpinion}
            open={showAssignDoctor}
            onOpenChange={setShowAssignDoctor}
            onSuccess={() => void dataQuery.refetch()}
          />

          <ReassignSecondOpinionDoctorDialog
            selectedSecondOpinion={selectedSecondOpinion}
            open={showReassignDoctor}
            onOpenChange={setShowReassignDoctor}
            onSuccess={() => void dataQuery.refetch()}
          />
        </>
      )}
    </div>
  );
}

// #region Dialog Components
interface B2BSecondOpinionDetailsDialogProps {
  selectedSecondOpinion: SecondOpinionRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const timelineTypeLabels = {
  CREATED: 'Case Created',
  FINISHED: 'Case Finished',
  DOCTOR_ASSIGNED: 'Doctor Assigned',
  DOCTOR_CONFIRMED: 'Doctor Confirmed',
  MORE_INFO_NEEDED: 'More Info Needed',
  INFO_SUBMITTED: 'Additional Info Submitted',
  REFERRED_TO_ANOTHER_DOCTOR: 'Referred to Another Doctor',
};

const timelineTypeIcons = {
  CREATED: Calendar,
  FINISHED: CheckCircle,
  DOCTOR_ASSIGNED: Stethoscope,
  DOCTOR_CONFIRMED: CheckCircle,
  MORE_INFO_NEEDED: MessageSquare,
  INFO_SUBMITTED: FileText,
  REFERRED_TO_ANOTHER_DOCTOR: User,
};

const formatTimelineContext = (type: string, context: string | null) => {
  if (!context) return null;

  try {
    const parsed = JSON.parse(context);
    switch (type) {
      case 'CREATED':
        return `Created by ${displayName(parsed.patient)}`;
      case 'DOCTOR_ASSIGNED':
        return `Doctor assigned to handle the case`;
      case 'DOCTOR_CONFIRMED':
        return `Doctor confirmed they will handle the case`;
      case 'FINISHED':
        return `Case was completed`;
      case 'MORE_INFO_NEEDED':
        return (
          parsed.requestedInfo || 'Doctor requested additional information'
        );
      case 'INFO_SUBMITTED':
        return parsed.details || 'Patient submitted additional information';
      case 'REFERRED_TO_ANOTHER_DOCTOR':
        return (
          parsed.reason || 'Referred to another doctor for specialized care'
        );
      default:
        return context;
    }
  } catch {
    return context;
  }
};

function B2BSecondOpinionDetailsDialog({
  selectedSecondOpinion,
  open,
  onOpenChange,
}: B2BSecondOpinionDetailsDialogProps) {
  const referringDoctorName = getDoctorName(
    selectedSecondOpinion.timeline,
    'CREATED',
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-7xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hash className="h-5 w-5" />
            Case #{selectedSecondOpinion.counter}
            {selectedSecondOpinion.urgent && (
              <Badge variant="destructive" className="ml-2">
                <AlertTriangle className="mr-1 h-3 w-3" />
                Urgent
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <PatientInfo patient={selectedSecondOpinion.patient} />

            <div className="bg-muted/50 rounded-lg p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold">
                <FileText className="h-4 w-4" />
                Case Information
              </h3>
              <B2BSecondOpinionInfo
                secondOpinion={selectedSecondOpinion}
                referringDoctorName={referringDoctorName}
              />
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold">
                <MessageSquare className="h-4 w-4" />
                Description
              </h3>
              <p className="text-muted-foreground warp-break-words text-sm whitespace-pre-wrap">
                {selectedSecondOpinion.description}
              </p>
            </div>

            {selectedSecondOpinion.files.length > 0 && (
              <AttachedFiles files={selectedSecondOpinion.files} />
            )}
          </div>

          <B2BSecondOpinionTimeline secondOpinion={selectedSecondOpinion} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
// #endregion

// #region Sub-components
function B2BSecondOpinionInfo({
  secondOpinion,
  referringDoctorName,
}: {
  secondOpinion: SecondOpinionRecord;
  referringDoctorName?: string;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Activity className="text-muted-foreground h-4 w-4" />
        <span className="text-sm font-medium">Status:</span>
        <ReservationStatus.Root status={secondOpinion.status} />
      </div>
      <div className="flex items-center gap-2">
        <Building2 className="text-muted-foreground h-4 w-4" />
        <span className="text-sm font-medium">Company:</span>
        <span className="text-sm">
          {secondOpinion.patient.b2bProfile?.name ?? '—'}
        </span>
      </div>
      {referringDoctorName && (
        <div className="flex items-center gap-2">
          <User className="text-muted-foreground h-4 w-4" />
          <span className="text-sm font-medium">Referring Doctor:</span>
          <span className="text-sm">{referringDoctorName}</span>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Stethoscope className="text-muted-foreground h-4 w-4" />
        <span className="text-sm font-medium">Speciality:</span>
        <span className="text-sm">{secondOpinion.speciality.name}</span>
      </div>
      <div className="flex items-center gap-2">
        <Calendar className="text-muted-foreground h-4 w-4" />
        <span className="text-sm font-medium">Created:</span>
        <span className="text-sm">
          {formatDateTimeShort(secondOpinion.createdAt)}
        </span>
      </div>
      {secondOpinion.doctor && (
        <div className="flex items-center gap-2">
          <User className="text-muted-foreground h-4 w-4" />
          <span className="text-sm font-medium">Assigned Doctor:</span>
          <span className="text-sm">
            {secondOpinion.doctor.firstName} {secondOpinion.doctor.lastName}
          </span>
        </div>
      )}
    </div>
  );
}

function B2BSecondOpinionTimeline({
  secondOpinion,
}: {
  secondOpinion: SecondOpinionRecord;
}) {
  return (
    <div className="space-y-4">
      <div className="bg-muted/50 rounded-lg p-4">
        <h3 className="mb-3 flex items-center gap-2 font-semibold">
          <Clock className="h-4 w-4" />
          Case Timeline
        </h3>
        <div className="max-h-96 space-y-3 overflow-y-auto">
          {secondOpinion.timeline?.length > 0 ? (
            secondOpinion.timeline.map((entry) => {
              const IconComponent =
                timelineTypeIcons[
                  entry.type as keyof typeof timelineTypeIcons
                ] || Activity;
              return (
                <div
                  key={entry.id}
                  className="border-border flex gap-3 border-b pb-3 last:border-b-0"
                >
                  <div className="shrink-0">
                    <div className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-full">
                      <IconComponent className="text-primary h-4 w-4" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {timelineTypeLabels[
                          entry.type as keyof typeof timelineTypeLabels
                        ] || entry.type}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {formatDateTimeShort(entry.createdAt)}
                      </span>
                    </div>
                    {entry.context && (
                      <p className="text-muted-foreground text-xs">
                        {formatTimelineContext(entry.type, entry.context)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-muted-foreground py-8 text-center">
              <Activity className="mx-auto mb-2 h-8 w-8 opacity-50" />
              <p className="text-sm">No timeline entries yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
// #endregion
